import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toDecimalPoint } from '../../../utils/decimalUtils';
import DecimalInput from '../../fields/DecimalInput';
import {
  DATUM_OPTIONS,
  DEFAULT_SK63_DISTRICT,
  DEGREES,
  FORMAT_OPTIONS,
  SK63,
  SK63_DISTRICTS,
  WGS84,
  decimalToDms,
  dmsToDecimal,
  fromWgs84,
  nearestSk63Zone,
  parseDegrees,
  sk63Errors,
  sk63ToWgs84,
  toWgs84,
  wgs84ToSk63,
} from './coordinateFormats';

const EMPTY_DMS = { degrees: '', minutes: '', seconds: '' };

const DMS_PARTS = [
  { part: 'degrees', sign: '°', name: 'градусы' },
  { part: 'minutes', sign: '′', name: 'минуты' },
  { part: 'seconds', sign: '″', name: 'секунды' },
];

// Район СК-63 обычно один на всю работу — запоминаем его между открытиями
const SK63_DISTRICT_KEY = 'coords.sk63District';

const readSavedDistrict = () => {
  try {
    const saved = window.localStorage.getItem(SK63_DISTRICT_KEY);
    return SK63_DISTRICTS.some((district) => district.id === saved) ? saved : DEFAULT_SK63_DISTRICT;
  } catch {
    return DEFAULT_SK63_DISTRICT;
  }
};

const saveDistrict = (districtId) => {
  try {
    window.localStorage.setItem(SK63_DISTRICT_KEY, districtId);
  } catch {
    // Без сохранения просто выберут район ещё раз
  }
};

const MapModal = ({ showMapModal, mapRef, mapPosition, closeMap, getCurrentLocation, selectCoordinates, updateMapPosition }) => {
  // Формат записи и система координат — только способ ввода и показа.
  // В карточку координаты уходят как прежде: WGS-84, десятичные градусы.
  const [format, setFormat] = useState(DEGREES);
  const [datum, setDatum] = useState(WGS84);
  const [sk63District, setSk63District] = useState(readSavedDistrict);
  const [sk63ZoneNumber, setSk63ZoneNumber] = useState(null);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [latDms, setLatDms] = useState(EMPTY_DMS);
  const [lngDms, setLngDms] = useState(EMPTY_DMS);
  const [latError, setLatError] = useState('');
  const [lngError, setLngError] = useState('');
  const isEditingRef = useRef({ lat: false, lng: false });
  const latInputRef = useRef(null);
  const lngInputRef = useRef(null);

  const isSk63 = datum === SK63;
  const sk63Zones = (SK63_DISTRICTS.find((district) => district.id === sk63District) || SK63_DISTRICTS[0]).zones;

  // Показываем точку карты в выбранной системе и в выбранном формате.
  // Для СК-63 в тех же полях: «широта» — X (на север), «долгота» — Y (на восток).
  const showPosition = useCallback(async (position) => {
    const lat = parseDegrees(position && position.lat);
    const lon = parseDegrees(position && position.lng);
    if (lat === null || lon === null) {
      setManualLat(''); setManualLng('');
      setLatDms(EMPTY_DMS); setLngDms(EMPTY_DMS);
      return;
    }
    if (datum === SK63) {
      if (sk63ZoneNumber === null) return;
      const shown = await wgs84ToSk63({ lat, lon }, sk63District, sk63ZoneNumber);
      setManualLat(shown.x.toFixed(2));
      setManualLng(shown.y.toFixed(2));
      return;
    }
    const shown = await fromWgs84({ lat, lon }, datum);
    setManualLat(shown.lat.toFixed(6));
    setManualLng(shown.lon.toFixed(6));
    setLatDms(decimalToDms(shown.lat));
    setLngDms(decimalToDms(shown.lon));
  }, [datum, sk63District, sk63ZoneNumber]);

  // Синхронизируем поля ввода с позицией карты (только если не редактируются)
  useEffect(() => {
    if (mapPosition && !isEditingRef.current.lat && !isEditingRef.current.lng) {
      showPosition(mapPosition);
    }
  }, [mapPosition, showPosition]);

  // Зона СК-63, пока её не выбрали: та, куда попадает текущая точка карты
  useEffect(() => {
    if (isSk63 && sk63ZoneNumber === null) {
      setSk63ZoneNumber(nearestSk63Zone(sk63District, parseDegrees(mapPosition && mapPosition.lng)));
    }
  }, [isSk63, sk63ZoneNumber, sk63District, mapPosition]);

  // Значения из полей — в десятичные градусы выбранной системы
  const readFields = () => ({
    lat: format === DEGREES ? parseDegrees(manualLat) : dmsToDecimal(latDms),
    lon: format === DEGREES ? parseDegrees(manualLng) : dmsToDecimal(lngDms),
  });

  // X и Y СК-63 — в WGS-84. null — если поля с ошибкой
  const readSk63 = async () => {
    const point = { x: parseDegrees(manualLat), y: parseDegrees(manualLng) };
    const errors = sk63Errors(point, sk63District, sk63ZoneNumber);
    setLatError(errors.x);
    setLngError(errors.y);
    if (errors.x || errors.y) return null;
    const wgs = await sk63ToWgs84(point, sk63District, sk63ZoneNumber);
    if (!wgs || !Number.isFinite(wgs.lat) || !Number.isFinite(wgs.lon)) {
      setLatError('Не удалось пересчитать — проверьте район, зону, X и Y');
      return null;
    }
    return wgs;
  };

  const readGeographic = async () => {
    const { lat, lon } = readFields();
    const latMessage = lat === null ? 'Введите широту' : (lat < -90 || lat > 90 ? 'Широта должна быть от -90 до 90' : '');
    const lonMessage = lon === null ? 'Введите долготу' : (lon < -180 || lon > 180 ? 'Долгота должна быть от -180 до 180' : '');
    setLatError(latMessage);
    setLngError(lonMessage);
    if (latMessage || lonMessage) return null;
    return toWgs84({ lat, lon }, datum);
  };

  // Применяем введённое: пересчитываем в WGS-84 и двигаем карту
  const applyFields = async () => {
    const wgs = isSk63 ? await readSk63() : await readGeographic();
    if (!wgs) return;
    isEditingRef.current = { lat: false, lng: false };
    updateMapPosition(parseFloat(wgs.lat.toFixed(6)), parseFloat(wgs.lon.toFixed(6)));
  };

  const handleDecimalChange = (setValue, field) => (e) => {
    const value = toDecimalPoint(e.target.value);
    // Цифры, знак минус и разделитель дробной части — точка или запятая
    if (value === '' || /^-?\d*[.,]?\d*$/.test(value)) {
      isEditingRef.current[field] = true;
      setValue(value);
    }
  };

  const handleDmsChange = (setValue, current, part, field) => (e) => {
    const value = toDecimalPoint(e.target.value);
    const allowed = part === 'degrees' ? /^-?\d*$/ : /^\d*[.,]?\d*$/;
    if (value === '' || allowed.test(value)) {
      isEditingRef.current[field] = true;
      setValue({ ...current, [part]: value });
    }
  };

  const handleBlur = () => { applyFields(); };

  const handleApplyManualCoordinates = () => {
    const activeElement = document.activeElement;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
    applyFields();
  };

  // Смена формата, системы, района или зоны: показываем ту же точку по-новому
  const resetEditing = () => {
    isEditingRef.current = { lat: false, lng: false };
    setLatError(''); setLngError('');
  };

  const changeFormat = (value) => {
    resetEditing();
    setFormat(value);
  };

  const changeDatum = (value) => {
    resetEditing();
    setDatum(value);
  };

  const changeSk63District = (value) => {
    resetEditing();
    saveDistrict(value);
    setSk63District(value);
    setSk63ZoneNumber(null);
  };

  const changeSk63Zone = (value) => {
    resetEditing();
    setSk63ZoneNumber(Number(value));
  };

  // Закрытие по Escape
  useEffect(() => {
    if (!showMapModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMap();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showMapModal, closeMap]);

  if (!showMapModal) return null;

  const coordinateField = (label, field, placeholder) => {
    const isLat = field === 'lat';
    const error = isLat ? latError : lngError;
    const controlClass = `coords-control${error ? ' coords-control--error' : ''}`;
    const inputRef = isLat ? latInputRef : lngInputRef;
    return (
      <div className="coords-field" key={field}>
        <label className="coords-field__label" htmlFor={`coords-${field}`}>{label}</label>
        {format === DEGREES || isSk63 ? (
          <DecimalInput
            id={`coords-${field}`}
            ref={inputRef}
            className={controlClass}
            value={isLat ? manualLat : manualLng}
            onChange={handleDecimalChange(isLat ? setManualLat : setManualLng, field)}
            onBlur={handleBlur}
            placeholder={placeholder}
          />
        ) : (
          <div className="coords-dms">
            {DMS_PARTS.map(({ part, sign, name }, index) => {
              const Field = part === 'degrees' ? 'input' : DecimalInput;
              return (
                <div className="coords-dms__cell" key={part}>
                  <Field
                    ref={index === 0 ? inputRef : null}
                    type="text"
                    className={controlClass}
                    value={(isLat ? latDms : lngDms)[part]}
                    onChange={handleDmsChange(isLat ? setLatDms : setLngDms, isLat ? latDms : lngDms, part, field)}
                    onBlur={handleBlur}
                    aria-label={`${label}, ${name}`}
                  />
                  <span className="coords-dms__unit">{sign}</span>
                </div>
              );
            })}
          </div>
        )}
        {error && <div className="coords-error">{error}</div>}
      </div>
    );
  };

  const selectField = (id, label, value, onChange, options) => (
    <div className="coords-field">
      <label className="coords-field__label" htmlFor={id}>{label}</label>
      <select
        id={id}
        className="coords-control"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="map-modal-overlay" onClick={closeMap}>
      <div className="map-modal" onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h3>Выбор координат</h3>
          <button className="map-modal-close" onClick={closeMap}>×</button>
        </div>
        <div className="map-modal-content">
          <div className="coords-panel">
            <div className="coords-row">
              {/* В СК-63 формат один — метры, выбирать нечего */}
              {!isSk63 && selectField('coords-format', 'Формат ввода', format, changeFormat, FORMAT_OPTIONS)}
              {selectField('coords-datum', 'Система координат', datum, changeDatum, DATUM_OPTIONS)}
            </div>
            {isSk63 && (
              <div className="coords-row">
                {selectField('coords-sk63-district', 'Район', sk63District, changeSk63District,
                  SK63_DISTRICTS.map((district) => ({ value: district.id, label: district.label })))}
                {selectField('coords-sk63-zone', 'Зона', sk63ZoneNumber ?? '', changeSk63Zone,
                  sk63Zones.map((zone) => ({ value: zone.zone, label: `Зона ${zone.zone}` })))}
              </div>
            )}
            <div className="coords-row">
              {isSk63 ? (
                <>
                  {coordinateField('X (на север), м', 'lat', 'например, 6123456,78')}
                  {/* Y начинается с номера зоны (у нулевой зоны — без него) */}
                  {coordinateField('Y (на восток), м', 'lng', `например, ${sk63ZoneNumber || ''}345678,90`)}
                </>
              ) : (
                <>
                  {coordinateField('Широта', 'lat', 'от -90 до 90')}
                  {coordinateField('Долгота', 'lng', 'от -180 до 180')}
                </>
              )}
            </div>
          </div>
          <div className="map-container">
            <div
              ref={mapRef}
              style={{
                width: '100%',
                height: '400px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                margin: '0 auto'
              }}
            />
            <div className="map-buttons">
              <button
                className="btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleApplyManualCoordinates}
              >
                Применить
              </button>
              <button
                className="btn"
                onClick={getCurrentLocation}
              >
                Моё местоположение
              </button>
              <button
                className="btn"
                onClick={() => selectCoordinates(mapPosition.lat, mapPosition.lng)}
                style={{ fontWeight: 'bold' }}
                disabled={latError !== '' || lngError !== '' || !mapPosition || !mapPosition.lat || !mapPosition.lng}
              >
                Выбрать координаты
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapModal;
